import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';

import { AuthContext } from '~/auth';

import { NAVIGATION_LINKS } from '~/consts/navigation';

import { navigationLinkHrefForUser } from '~/utils/navigationLinks';

interface SpotlightEntry {
  path: string;
  label: string;
  section: string;
  keywords?: string;
}

interface SpotlightContextValue {
  register: (entry: SpotlightEntry) => void;
  getRoutes: () => Array<SpotlightEntry>;
}

const SpotlightContext = createContext<SpotlightContextValue>({
  register: () => undefined,
  getRoutes: () => [],
});

const STATIC_REGISTRY: Array<SpotlightEntry> = [];

export function preRegisterSpotlightRoutes(entries: Array<SpotlightEntry>): void {
  for (const entry of entries) {
    if (!STATIC_REGISTRY.some((r) => r.path === entry.path)) {
      STATIC_REGISTRY.push(entry);
    }
  }
}

export function useSpotlightRoutes() {
  return useContext(SpotlightContext).getRoutes;
}

/**
 * Self-registration hook. Call this inside any component that owns navigable sub-routes
 * (tab containers, sidebar pages, hash-tab pages, query-param tab pages, etc.).
 *
 * Routes are registered once on mount. Because registration lives next to the navigation
 * definition, spotlight stays in sync automatically as tabs/items are added or removed.
 *
 * @example
 * // Inside a tab container
 * useRegisterSpotlightRoutes(expandTabRoutes('/finance', 'Finance', TabsMap));
 *
 * // Inside a hash-tab page
 * useRegisterSpotlightRoutes([
 *   { path: '/accommodation/properties#unmapped_suppliers', label: 'Accommodation / Unmapped Suppliers', section: 'Accommodation' },
 * ]);
 */
export function useRegisterSpotlightRoutes(entries: Array<SpotlightEntry>) {
  const { register } = useContext(SpotlightContext);
  useEffect(() => {
    entries.forEach(register);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Routes are static — register once on mount
}

export function titleCase(str: string): string {
  return str
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * Strips optional route params and returns the static portion of a path.
 * Returns null if the path contains required params.
 */
function extractStaticPath(routePath: string): string | null {
  const segments = routePath.split('/');
  const staticSegments: Array<string> = [];

  for (const segment of segments) {
    if (segment.startsWith(':')) {
      const isOptional = segment.endsWith('?') || /\)\?$/.test(segment);
      if (isOptional) {
        break;
      }
      return null;
    }
    staticSegments.push(segment);
  }

  const path = staticSegments.join('/');
  return path || '/';
}

function deriveLabel(path: string): string {
  const segments = path.split('/').filter(Boolean);
  if (segments.length === 0) {
    return 'Home';
  }
  return segments.map(titleCase).join(' / ');
}

function deriveSection(path: string): string {
  const firstSegment = path.split('/').filter(Boolean)[0];
  return firstSegment ? titleCase(firstSegment) : 'Home';
}

interface SpotlightProviderProps {
  children: React.ReactNode;
}

export function SpotlightProvider({ children }: SpotlightProviderProps) {
  const { user } = useContext(AuthContext);
  const registryRef = useRef<Map<string, SpotlightEntry> | null>(null);
  const cachedRoutesRef = useRef<Array<SpotlightEntry> | null>(null);

  // Lazy-initialize the registry with NAVIGATION_LINKS so they take priority
  // over auto-derived labels from RouteScanner
  if (registryRef.current === null) {
    const registry = new Map<string, SpotlightEntry>();

    for (const link of NAVIGATION_LINKS) {
      // Same rule the top nav itself applies (navigationLinkHrefForUser): an askRichard-eligible
      // user is sent straight to the unified shell, so a Spotlight search result lands them in
      // the same place the sidebar link would, not the separate legacy chat page.
      const href = link.href && navigationLinkHrefForUser(link, user);
      if (href) {
        registry.set(href, {
          path: href,
          label: link.label,
          section: link.label,
        });
      }
      for (const subLink of link.subLinks ?? []) {
        const subHref = navigationLinkHrefForUser(subLink, user);
        registry.set(subHref, {
          path: subHref,
          label: subLink.label,
          section: link.label,
        });
      }
    }

    for (const route of STATIC_REGISTRY) {
      if (!registry.has(route.path)) {
        registry.set(route.path, route);
      }
    }

    registryRef.current = registry;
  }

  const register = useCallback((entry: SpotlightEntry) => {
    if (!registryRef.current.has(entry.path)) {
      registryRef.current.set(entry.path, entry);
      cachedRoutesRef.current = null;
    }
  }, []);

  const getRoutes = useCallback(() => {
    if (!cachedRoutesRef.current) {
      cachedRoutesRef.current = Array.from(registryRef.current.values());
    }
    return cachedRoutesRef.current;
  }, []);

  const value = useMemo(() => ({ register, getRoutes }), [register, getRoutes]);

  return <SpotlightContext.Provider value={value}>{children}</SpotlightContext.Provider>;
}

/**
 * Wraps children and recursively scans for React Router <Route> elements,
 * automatically registering their static paths in the spotlight.
 */
export function RouteScanner({ children }: { children: React.ReactNode }) {
  const { register } = useContext(SpotlightContext);

  useEffect(() => {
    function scan(nodes: React.ReactNode) {
      React.Children.forEach(nodes, (child) => {
        if (!React.isValidElement(child)) {
          return;
        }

        const path = (child.props as Record<string, unknown>)?.path;
        if (typeof path === 'string') {
          const staticPath = extractStaticPath(path);
          if (staticPath) {
            register({
              path: staticPath,
              label: deriveLabel(staticPath),
              section: deriveSection(staticPath),
            });
          }
        }

        const childChildren = (child.props as Record<string, unknown>)?.children;
        if (childChildren) {
          scan(childChildren as React.ReactNode);
        }
      });
    }

    scan(children);
  }, []); // Route structure is static, scan once on mount

  return <>{children}</>;
}

/**
 * Expands a tab/section-based route into individual spotlight entries.
 * Accepts a base path and a tabs map where keys are tab slugs and values have a `label` or `title` or `name` field.
 * Uses the `slug` field on each tab if present, falling back to the map key.
 *
 * Call this via `useRegisterSpotlightRoutes` inside the tab container component so that
 * spotlight stays in sync as new tabs are added to the map.
 */
export function expandTabRoutes(
  basePath: string,
  section: string,
  tabs: Record<string, { label?: string; title?: string; name?: string; slug?: string; keywords?: string }>,
): Array<SpotlightEntry> {
  return Object.entries(tabs).map(([key, tab]) => ({
    path: `${basePath}/${tab.slug ?? key}`,
    label: `${section} / ${tab.label ?? tab.title ?? tab.name ?? key}`,
    section,
    keywords: tab.keywords,
  }));
}

/**
 * Expands cruise-style menu items (href + name) into spotlight entries.
 *
 * Call this via `useRegisterSpotlightRoutes` inside the menu host component.
 */
export function expandMenuRoutes(
  section: string,
  menus: ReadonlyArray<{ name: string; href: string }>,
): Array<SpotlightEntry> {
  return menus.map((item) => ({
    path: item.href,
    label: `${section} / ${item.name}`,
    section,
  }));
}

export type { SpotlightEntry };
